import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import DataTable, { createDataRow, HeadCell } from "../../components/DataTable";
import { Chip, Button, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
	useRewardsEngine,
	useCreateRewardConfig,
	useUpdateRewardConfig,
	useDeleteRewardConfig,
} from "../../hooks/useRewardsEngine";
import RewardsConfigModal, {
	RewardsConfigFormData,
} from "../../components/modals/RewardsConfiguration/RewardsConfigurationModal";
import { useUserStore } from "../../stores/user.store";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import {
	RewardConfigurationType,
	RewardsEngine,
} from "../../domain/reward.interface";
import GenericAlertModal from "../../components/modals/GenericAlertModal";

type ModalMode = "create" | "edit" | "view";

const RewardsConfigPage: FC = () => {
	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const {
		data: rewardsEngineList,
		isLoading,
		isFetching,
	} = useRewardsEngine(true);
	const { mutateAsync: createRewardConfig, isPending: isSaving } =
		useCreateRewardConfig();
	const { mutateAsync: updateRewardConfig, isPending: isUpdating } =
		useUpdateRewardConfig();
	const { mutateAsync: deleteRewardConfig, isPending: isDeleting } =
		useDeleteRewardConfig();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState<ModalMode>("create");
	const [editingReward, setEditingReward] = useState<RewardsEngine | null>(
		null,
	);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [rewardToDelete, setRewardToDelete] = useState<string | null>(null);

	const handleOpenModal = (mode: ModalMode = "create") => {
		setModalMode(mode);
		if (mode === "create") {
			setEditingReward(null);
		}
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setEditingReward(null);
		setModalMode("create");
	};

	const handleView = (reward: RewardsEngine) => {
		setEditingReward(reward);
		handleOpenModal("view");
	};

	const handleEdit = (reward: RewardsEngine) => {
		setEditingReward(reward);
		handleOpenModal("edit");
	};

	const handleClone = (reward: RewardsEngine) => {
		// Create a copy without the _id and timestamps to create a new config
		const clonedReward: RewardsEngine = {
			...reward,
			event: `${reward.event} (Copy)`, // Add indicator that it's a copy
			_id: undefined,
			createdAt: undefined,
			updatedAt: undefined,
			__v: undefined,
		};

		// Set the cloned reward and open modal in create mode
		// Don't use handleOpenModal because it clears editingReward for create mode
		setEditingReward(clonedReward);
		setModalMode("create");
		setIsModalOpen(true);
	};

	const handleDeleteClick = (id: string) => {
		setRewardToDelete(id);
		setDeleteConfirmOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!rewardToDelete) return;

		try {
			await deleteRewardConfig(rewardToDelete);
			showSnackBar("Reward configuration deleted successfully", "success");
			setDeleteConfirmOpen(false);
			setRewardToDelete(null);
		} catch (error) {
			showSnackBar("Error deleting reward config: " + error, "error");
		}
	};

	const handleSave = async (data: RewardsConfigFormData) => {
		const {
			fixedLadderConfiguration,
			programmableLadderConfiguration,
			invalidResult,
			validValue,
			invalidValue,
			customLadderReset,
			...rest
		} = data;

		const cleanedData = {
			...rest,
			maxOccurrences: Number(data.maxOccurrences),
			...(data.rewardConfigurationType ===
				RewardConfigurationType.FixedLadder && {
				fixedLadderConfiguration: [
					...(fixedLadderConfiguration || []).map((s) => ({
						...s,
						points: Number(s.points),
					})),
				],
			}),
			...(data.rewardConfigurationType ===
				RewardConfigurationType.ProgrammableLadder && {
				programmableLadderConfiguration: programmableLadderConfiguration
					? [programmableLadderConfiguration]
					: undefined,
			}),
			...(data.rewardBasedOnResult && {
				invalidResult: invalidResult
					? {
							...invalidResult,
							specificIndex: Number(invalidResult.specificIndex), // Convert to number
						}
					: undefined,
				validValue,
				invalidValue,
			}),
			...(customLadderReset?.enabled && {
				customLadderReset: {
					...customLadderReset,
					conditions: customLadderReset.conditions.map((condition) => ({
						...condition,
						relativePosition: Number(condition.relativePosition),
						targetStep:
							condition?.targetStep !== undefined
								? Number(condition?.targetStep)
								: undefined,
					})),
					recovery: {
						...customLadderReset.recovery,
						enabled: customLadderReset?.recovery?.enabled || false,
						recoverInSteps:
							customLadderReset?.recovery?.recoverInSteps !== undefined
								? Number(customLadderReset?.recovery?.recoverInSteps)
								: undefined,
						resetToStep:
							customLadderReset?.recovery?.resetToStep !== undefined
								? Number(customLadderReset?.recovery?.resetToStep)
								: undefined,
					},
				},
			}),
			accountId: userInfo?.accountId,
			createdBy: userInfo?.id,
		};

		try {
			if (editingReward && editingReward._id) {
				await updateRewardConfig({ id: editingReward._id, data: cleanedData });
				showSnackBar("Reward configuration updated successfully", "success");
			} else {
				await createRewardConfig(cleanedData);
				showSnackBar("Reward configuration created successfully", "success");
			}
			handleCloseModal();
		} catch (error) {
			showSnackBar(
				`Error ${editingReward && editingReward._id ? "updating" : "creating"} reward config: ` +
					error,
				"error",
			);
		}
	};

	const rows = useMemo(() => {
		if (!rewardsEngineList || rewardsEngineList.length === 0) return [];

		return rewardsEngineList.map((reward, index) => {
			const row = createDataRow(index, {
				eventType: reward.eventType,
				event: reward.event,
				rewardConfigurationType: reward.rewardConfigurationType,
				configurationDetails: (
					<Chip
						label={`Ladder Steps: ${
							reward.fixedLadderConfiguration
								? reward.fixedLadderConfiguration.length
								: reward.programmableLadderConfiguration
									? "Programmable Ladder"
									: "No Details"
						}`}
					/>
				),
				actions: (
					<Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
						<Tooltip title="View">
							<IconButton
								size="small"
								onClick={() => handleView(reward)}
								color="primary"
							>
								<VisibilityIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title="Edit">
							<IconButton
								size="small"
								onClick={() => handleEdit(reward)}
								color="primary"
							>
								<EditIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title="Clone">
							<IconButton
								size="small"
								onClick={() => handleClone(reward)}
								color="primary"
							>
								<ContentCopyIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title="Delete">
							<IconButton
								size="small"
								onClick={() => reward._id && handleDeleteClick(reward._id)}
								color="error"
								disabled={!reward._id}
							>
								<DeleteIcon />
							</IconButton>
						</Tooltip>
					</Box>
				),
			});
			return row;
		});
	}, [rewardsEngineList]);

	const headCells: HeadCell[] = [
		{
			id: "eventType",
			isSortable: true,
			disablePadding: false,
			label: "Event Type",
			align: "left",
			width: "20%",
		},
		{
			id: "event",
			isSortable: true,
			disablePadding: false,
			label: "Event",
			align: "left",
			width: "20%",
		},
		{
			id: "rewardConfigurationType",
			isSortable: true,
			disablePadding: false,
			label: "Configuration Type",
			align: "left",
			width: "20%",
		},
		{
			id: "configurationDetails",
			isSortable: false,
			disablePadding: false,
			label: "Details",
			align: "left",
			width: "30%",
		},
		{
			id: "actions",
			isSortable: false,
			disablePadding: true,
			label: "Actions",
			align: "center",
			width: "10%",
		},
	];

	// Removed setPageTitle as it is not defined. Add Helmet or document.title if needed.
	useEffect(() => {
		// Optionally set document.title here:
		// document.title = "Rewards Configuration";
	}, []);

	return (
		<Box className="w-full overflow-y-auto">
			{/* Use React.Fragment to wrap non-MUI elements for valid JSX */}
			<React.Fragment>
				<div className="full-width-container mx-auto">
					<div className="flex flex-col items-center justify-start min-h-screen">
						<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => handleOpenModal("create")}
							>
								Create New Configuration
							</Button>
						</Box>

						<Card sx={{ width: "100%", borderRadius: "8px" }}>
							<CardContent
								sx={{
									display: "flex",
									flexDirection: "column",
									rowGap: "20px",
								}}
							>
								<DataTable
									data={rows}
									headCells={headCells}
									order="asc"
									orderBy="event"
									rowsPerPage={10}
									page={0}
									total={rewardsEngineList?.length || 0}
									isLoading={isLoading || isFetching}
								/>
							</CardContent>
						</Card>
					</div>
				</div>
				<RewardsConfigModal
					open={isModalOpen}
					onClose={handleCloseModal}
					onSave={handleSave}
					isPending={isSaving || isUpdating}
					mode={modalMode}
					initialData={editingReward || undefined}
				/>
				{deleteConfirmOpen && (
					<GenericAlertModal
						title="Delete Reward Configuration"
						description="Are you sure you want to delete this reward configuration? This action cannot be undone."
						submitText="Delete"
						cancelText="Cancel"
						onClick={handleDeleteConfirm}
						onCancel={() => {
							setDeleteConfirmOpen(false);
							setRewardToDelete(null);
						}}
						isLoading={isDeleting}
					/>
				)}
			</React.Fragment>
		</Box>
	);
};

export default RewardsConfigPage;
